import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Replaces `Pages/Error/Error404.cshtml`.
 *
 * Rendered inside the app shell, as the Razor page was — a 404 reached while
 * signed in should keep the navigation, so the user can go somewhere else
 * without using the back button.
 *
 * The blob backdrop is the design's own: eight `<path>` elements whose shapes
 * and colours come entirely from `_logo.scss` keyframes, so there is nothing to
 * draw here and no script to port. The two custom properties set the palette —
 * danger and warning for this page, as the original did.
 */

/** Eight layers, four shapes each rendered twice; the `alt` copies run offset. */
const BLOBS = [
  'blob-1',
  'blob-2',
  'blob-3',
  'blob-4',
  'blob-1 alt',
  'blob-2 alt',
  'blob-3 alt',
  'blob-4 alt',
];

export function NotFoundPage() {
  const { t } = useTranslation();

  return (
    // Height bounded to the viewport: the backdrop below is 45rem tall and
    // absolutely positioned, so without this the document grows past the fold and
    // the centred message lands below it — the page looks blank until you scroll.
    <div
      className="main-content hide-page-header d-flex justify-content-center align-items-center position-relative overflow-hidden"
      style={{ minHeight: 'calc(100vh - 13rem)' }}
    >
      <div
        className="logo-backdrop position-absolute translate-middle top-50 start-50 d-flex align-items-center justify-content-center"
        style={
          {
            width: '45rem',
            height: '45rem',
            '--blob-1': 'var(--danger-500)',
            '--blob-2': 'var(--warning-500)',
          } as React.CSSProperties
        }
        aria-hidden="true"
      >
        <div className="blobs">
          <svg viewBox="0 0 1200 1200">
            {BLOBS.map((className) => (
              <g className={`blob ${className}`} key={className}>
                <path />
              </g>
            ))}
          </svg>
        </div>
      </div>

      <div
        className="d-flex flex-column align-items-center justify-content-center text-center"
        style={{ zIndex: 2 }}
      >
        <h1
          className="page-error color-fusion-500"
          style={{ fontSize: '750%', fontWeight: 700 }}
        >
          {t('Error.Error', 'ERROR')} <span className="text-gradient">404</span>
          <small className="fw-500 d-block" style={{ fontSize: '40%' }}>
            {t('Error.404.Tagline', 'This page has wandered off.')}
          </small>
        </h1>
        <h3 className="fw-500 mb-4 text-muted">
          {t('Error.404.Message', 'We are sorry, but the page you are looking for does not exist.')}
        </h3>
        <Link to="/" className="btn btn-primary">
          {t('Error.BackHome', 'Back to home')}
        </Link>
      </div>
    </div>
  );
}
